import { useGetStatsHistoryQuery } from "../state/api";
import { buildDays, buildDaysFromLastN, numDaysToToday } from "../utils";

// Two modes, picked by the caller (not auto-detected):
// - Date-window (Trends page): pass startDate + endDate. Days array spans the calendar window
//   with `null` snapshots for gap days, so the chart can render an honest "what happened in
//   April" view including any holes.
// - Last-N (dashboard tiles): pass lastN. Fetches a generous over-window and returns the most
//   recent N real snapshot days (no zero-fill gaps). The unit becomes "measurements", which
//   stays meaningful regardless of how often the workspace is actually crawled.
export function useTrendsData({ workspaceNumber, startDate, endDate, lastN }) {
    const isCountMode = Number.isInteger(lastN) && lastN > 0;

    // For count mode, over-fetch generously: even a workspace that crawls weekly produces
    // ~30 snapshots per 30 calendar days today (the daily-scheduler bug — see
    // backlog/issue_workspace_stats_snapshot_without_crawl), but once that's fixed a weekly
    // site needs ~7×N days. Cap at 1y so a stale workspace doesn't trigger a huge fetch.
    const size = isCountMode
        ? Math.min(lastN * 7, 365)
        : numDaysToToday({ startIso: startDate });

    const { data, isLoading, isFetching, isError, error, refetch } = useGetStatsHistoryQuery(
        { workspaceNumber, size },
        { skip: workspaceNumber == null }
    );

    const snapshots = Array.isArray(data) ? data : [];
    const days = isCountMode
        ? buildDaysFromLastN(snapshots, lastN)
        : buildDays(startDate, endDate, snapshots);

    // A day with no snapshot is missing data, not zero — emit null so the charts (connectNulls)
    // bridge across it instead of diving to 0. (In count mode there are no gap days, so every
    // entry has a snapshot and `value` is always a real number.)
    const series = (picker) =>
        days.map(({ key, snapshot }) => ({
            dayKey: key,
            value: snapshot ? (picker(snapshot.stats || {}) ?? 0) : null,
        }));

    return { series, days, isLoading, isFetching, isError, error, refetch };
}
